import { useMemo, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders, fmtDate, calcAge, canEditStudentProfile } from "../profileUtils";
import s from "./ProfileTab.module.css";

const SEX_OPTIONS    = ["Male", "Female", "Other"];
const STATUS_OPTIONS = ["ENROLLED", "INACTIVE", "GRADUATED", "DROPPED"];

export default function ProfileTab({ student, gradeLevels = [], sections = [], onStudentUpdate }) {
  const sectionById    = useMemo(() => Object.fromEntries(sections.map(sec => [sec.id, sec])), [sections]);
  const gradeLevelById = useMemo(() => Object.fromEntries(gradeLevels.map(gl => [gl.id, gl])), [gradeLevels]);

  const currentSection = student?.currentSectionId ? sectionById[student.currentSectionId] : null;
  const currentGrade   = currentSection?.gradeLevelId ? gradeLevelById[currentSection.gradeLevelId] : null;
  const age            = calcAge(student?.birthdate);

  const [editing,   setEditing]   = useState(false);
  const [form,      setForm]      = useState({});
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState("");

  const canEdit = canEditStudentProfile() && !!onStudentUpdate;

  function startEdit() {
    setForm({
      firstName:  student?.firstName  || "",
      middleName: student?.middleName || "",
      lastName:   student?.lastName   || "",
      sex:        student?.sex        || "",
      birthdate:  student?.birthdate ? student.birthdate.slice(0, 10) : "",
      contact:    student?.contact    || "",
      address:    student?.address    || "",
      status:     student?.status     || "",
    });
    setSaveError("");
    setEditing(true);
  }

  function cancelEdit() { setEditing(false); setSaveError(""); }

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSave() {
    if (!student?.id) return;
    setSaving(true); setSaveError("");
    try {
      const res = await fetch(`${API_BASE}/api/students/${student.id}`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setEditing(false);
      onStudentUpdate?.(updated);
    } catch {
      setSaveError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  /* ── Edit mode ─────────────────────────────────────────────────────── */
  if (editing) {
    return (
      <div className={s.root}>
        <div className={s.editHeader}>
          <span className={s.sectionTitle}>Edit Biographical Information</span>
          <div className={s.editActions}>
            <button className={s.cancelBtn} onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
            <button className={s.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        {saveError && <div className={s.saveError}>{saveError}</div>}

        <div className={s.editGrid}>
          <div className={s.editField}>
            <label className={s.editLabel}>First Name</label>
            <input className={s.editInput} value={form.firstName} onChange={set("firstName")} />
          </div>
          <div className={s.editField}>
            <label className={s.editLabel}>Middle Name</label>
            <input className={s.editInput} value={form.middleName} onChange={set("middleName")} placeholder="optional" />
          </div>
          <div className={`${s.editField} ${s.editWide}`}>
            <label className={s.editLabel}>Last Name</label>
            <input className={s.editInput} value={form.lastName} onChange={set("lastName")} />
          </div>
          <div className={s.editField}>
            <label className={s.editLabel}>Sex</label>
            <select className={s.editSelect} value={form.sex} onChange={set("sex")}>
              <option value="">— select —</option>
              {SEX_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className={s.editField}>
            <label className={s.editLabel}>Birthdate</label>
            <input type="date" className={s.editInput} value={form.birthdate} onChange={set("birthdate")} />
          </div>
          <div className={s.editField}>
            <label className={s.editLabel}>Status</label>
            <select className={s.editSelect} value={form.status} onChange={set("status")}>
              <option value="">— select —</option>
              {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className={s.editField}>
            <label className={s.editLabel}>Contact</label>
            <input className={s.editInput} value={form.contact} onChange={set("contact")} placeholder="Phone number" />
          </div>
          <div className={`${s.editField} ${s.editWide}`}>
            <label className={s.editLabel}>Address</label>
            <textarea className={s.editTextarea} value={form.address} onChange={set("address")} rows={3} />
          </div>
        </div>

        <div className={s.readOnlyNote}>
          Student number and current section are managed separately.
        </div>
      </div>
    );
  }

  /* ── Read mode ─────────────────────────────────────────────────────── */
  const rows = [
    { label: "Full name",       value: [student?.firstName, student?.middleName, student?.lastName].filter(Boolean).join(" ") },
    { label: "Student number",  value: student?.studentNumber, mono: true },
    { label: "Status",          value: student?.status },
    { label: "Sex",             value: student?.sex },
    { label: "Birthdate",       value: fmtDate(student?.birthdate), sub: age != null ? `${age} years old` : null },
    { label: "Contact",         value: student?.contact },
    { label: "Address",         value: student?.address, wide: true },
    { label: "Current section", value: currentGrade && currentSection
        ? `${currentGrade.name} · ${currentSection.name}`
        : currentSection?.name || null },
  ];

  return (
    <div className={s.root}>
      <div className={s.editHeader}>
        <span className={s.sectionTitle}>Biographical Information</span>
        {canEdit && (
          <button className={s.editBtn} onClick={startEdit} type="button">
            Edit
          </button>
        )}
      </div>
      <dl className={s.grid}>
        {rows.map(({ label, value, mono, sub, wide }) => (
          <div key={label} className={`${s.item} ${wide ? s.wide : ""}`}>
            <dt className={s.label}>{label}</dt>
            <dd className={`${s.value} ${mono ? s.mono : ""}`}>
              {value || <span className={s.empty}>—</span>}
              {sub && <span className={s.sub}>{sub}</span>}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
